import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

console.log('Supabase URL:', supabaseUrl);
console.log('Supabase Anon Key:', supabaseAnonKey && supabaseAnonKey.substring(0, 25) + '...');

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase URL or Anon Key is missing');
}

// Create a Supabase client for database operations only
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  },
  realtime: {
    params: {
      eventsPerSecond: 1
    }
  },
  global: {
    fetch: (...args) => {
      // Add timeout to prevent hanging requests
      return fetch(...args, { signal: AbortSignal.timeout(10000) });
    }
  }
})

// Create a public client specifically for public data like leaderboards
export const supabasePublic = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  },
  global: {
    headers: {
      // Explicitly set role to public to ensure it works with RLS policies
      'X-Client-Info': 'public',
    },
    fetch: (...args) => {
      // Modify the request to work with public role
      const [url, options = {}] = args;
      const newOptions = {
        ...options,
        headers: {
          ...options.headers,
        }
      };
      
      return fetch(url, newOptions, { signal: AbortSignal.timeout(8000) });
    }
  }
})

// Helper function to generate a consistent UUID-like ID from Privy IDs
export const getDbUserId = (privyId) => {
  if (!privyId) return null;
  
  // Create a hash of the Privy ID (did:privy:xyz format)
  let hash = 0;
  for (let i = 0; i < privyId.length; i++) {
    hash = ((hash << 5) - hash) + privyId.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to positive hex string and pad
  const hex = Math.abs(hash).toString(16).padStart(32, '0');
  
  // Format as UUID
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-4${hex.slice(13,16)}-${hex.slice(16,20)}-${hex.slice(20,32)}`;
};

// Function to verify the Supabase connection 
export const verifySupabaseConnection = async () => {
  try {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), 5000);
    });

    const connectionPromise = (async () => {
      const start = Date.now();
      
      // First try a public query for leaderboard data since this should work with the new policy
      try {
        const { data, error } = await supabasePublic
          .from('BalloonScores')
          .select('count')
          .limit(1);
        
        if (!error) {
          const elapsed = Date.now() - start;
          console.log(`Successfully connected to Supabase (${elapsed}ms)`);
          return { connected: true, elapsed, publicAccess: true };
        }
      } catch (publicError) {
        console.log("Public access check failed:", publicError);
      }

      // If public access fails, try authenticated access
      const { data, error } = await supabase
        .from('BalloonScores')
        .select('count')
        .limit(1);
      
      const elapsed = Date.now() - start;
      
      if (error) {
        // Identify common authentication errors
        if (error.code === '401' || error.code === '403') {
          console.error(`Authentication error connecting to Supabase (${elapsed}ms):`, error);
          return { 
            connected: false, 
            error, 
            elapsed,
            reason: 'auth',
            message: 'Authentication failed. Using mock data instead.'
          };
        }
        
        // RLS or permission errors
        if (error.code === '42501' || error.message?.includes('permission denied')) {
          console.error(`Permission error connecting to Supabase (${elapsed}ms):`, error);
          return { 
            connected: false, 
            error, 
            elapsed,
            reason: 'permission',
            message: 'Permission denied. Using mock data instead.'
          };
        }
        
        console.error(`Error connecting to Supabase (${elapsed}ms):`, error);
        return { connected: false, error, elapsed };
      } else {
        console.log(`Successfully connected to Supabase (${elapsed}ms)`);
        return { connected: true, data, elapsed };
      }
    })();

    // Race between the connection attempt and the timeout
    return await Promise.race([connectionPromise, timeoutPromise]);
  } catch (err) {
    console.error('Unexpected error connecting to Supabase:', err);
    return { connected: false, error: err };
  }
};

// Improved function to handle creating/updating user scores without ON CONFLICT
export const safeUpsertScore = async (userData, scoreValue = 0, allTimeScoreValue = 0) => {
  if (!userData || !userData.user_id) {
    console.error('Invalid user data provided to safeUpsertScore');
    return { error: { message: 'Invalid user data' } };
  }

  try {
    // First check if record exists
    console.log('Checking if user exists in database:', userData.user_id);
    const { data: existingUser, error: checkError } = await supabase
      .from('BalloonScores')
      .select('id')
      .eq('user_id', userData.user_id)
      .maybeSingle();

    if (checkError) {
      console.error('Error checking if user exists:', checkError);
      return { error: checkError };
    }

    let result;
    if (existingUser) {
      // User exists, update record
      console.log('Updating existing user record');
      result = await supabase
        .from('BalloonScores')
        .update({
          display_name: userData.display_name,
          email: userData.email || '',
          score: scoreValue,
          all_time_score: allTimeScoreValue
        })
        .eq('user_id', userData.user_id)
        .select();
    } else {
      // User doesn't exist, insert new record
      console.log('Creating new user record');
      result = await supabase
        .from('BalloonScores')
        .insert([{
          user_id: userData.user_id,
          display_name: userData.display_name,
          email: userData.email || '',
          score: scoreValue,
          all_time_score: allTimeScoreValue
        }])
        .select();
    }

    return result;
  } catch (err) {
    console.error('Error in safeUpsertScore:', err);
    return { error: err };
  }
};

// Test the connection
verifySupabaseConnection()
  .then(result => {
    if (!result.connected) {
      console.warn('⚠️ Supabase connection test failed - leaderboard functionality may be limited');
    }
  })
  .catch(err => {
    console.error('Error during Supabase connection test:', err);
  });

// Helper function to generate mock leaderboard data for development
export const getMockLeaderboardData = () => {
  // Make it obvious these are mock entries by adding [MOCK] to names
  return [
    { id: 1, display_name: '[MOCK] TheBalloonMaster', score: 84, all_time_score: 220 },
    { id: 2, display_name: '[MOCK] PopStar', score: 76, all_time_score: 198 },
    { id: 3, display_name: '[MOCK] BalloonGenius', score: 65, all_time_score: 175 },
    { id: 4, display_name: '[MOCK] AirPopper', score: 42, all_time_score: 155 },
    { id: 5, display_name: '[MOCK] FloatingKing', score: 39, all_time_score: 129 },
    { id: 6, display_name: '[MOCK] SkyRider', score: 36, all_time_score: 112 },
    { id: 7, display_name: '[MOCK] DaPerformer', score: 32, all_time_score: 94 },
    { id: 8, display_name: '[MOCK] BurstMaster', score: 27, all_time_score: 88 },
    { id: 9, display_name: '[MOCK] HeliumHero', score: 21, all_time_score: 65 },
    { id: 10, display_name: '[MOCK] AirChampion', score: 18, all_time_score: 55 }
  ];
};
